import { crsPickerField, enhanceCrsPickers } from "../../core/ui/crs-picker.js";
function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
}

export const projectionManagerTool = {
  id: "projection-manager",
  name: "Projection Manager",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore?.(data); },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
};

function createRuntime(app) {
  const svc = app.services.projections;
  let container = null;
  const off = [];
  let lastPreviewState = {
    from: "EPSG:4326",
    to: "EPSG:3857",
    x: "",
    y: "",
  };
  // Catalog-wide check of every project's stored CRS (GET /api/project/crs-audit). The
  // project CRS is the source of truth for everything downstream, so a project with none, or
  // with fields that disagree, is worth seeing in one place.
  let audit = { status: "idle", data: null, error: "" };
  const AUDIT_ISSUE_TEXT = {
    unset: "no CRS set",
    placeholder: "only the EPSG:4326 default -- nobody chose a CRS",
    conflict: "fields disagree",
    stale: "some fields still hold an old or default value",
    invalid: "a field isn't an EPSG code",
  };

  async function loadAudit() {
    audit = { ...audit, status: "loading", error: "" };
    render();
    try {
      // Scoped to the active client -- each client's projects are checked on their own.
      const client = app.services?.context?.getActiveClient?.()?.slug || "";
      const resp = await fetch(`/api/project/crs-audit${client ? `?client=${encodeURIComponent(client)}` : ""}`, { headers: { Accept: "application/json" }, cache: "no-store" });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) throw new Error(payload?.error || `HTTP ${resp.status}`);
      audit = { status: "done", data: { ...payload, client }, error: "" };
    } catch (err) {
      audit = { status: "error", data: null, error: String(err?.message || err) };
    }
    render();
  }

  function auditCardHtml() {
    const d = audit.data;
    let body;
    if (audit.status === "loading") body = `<div class="small">Checking...</div>`;
    else if (audit.status === "error") body = `<div class="small">Check failed: ${esc(audit.error)}</div>`;
    else if (!d) body = `<div class="small">Not checked yet.</div>`;
    else {
      const flagged = (d.projects || []).filter((r) => r.issues.length);
      const summary = Object.entries(d.counts || {}).map(([k, n]) => `${n} ${esc(k)}`).join(", ");
      const rows = flagged.map((r) => `
        <div class="proj-custom-row" style="align-items:flex-start;">
          <div class="small" title="${esc(r.fields.map((f) => `${f.field} = ${f.raw}`).join("\n"))}">
            <b>${esc(r.code)}</b> ${esc(r.name !== r.code ? r.name : "")}<br/>
            ${esc(r.crs && !r.placeholder ? r.crs : "--")} &middot; ${esc(r.issues.map((i) => AUDIT_ISSUE_TEXT[i] || i).join("; "))}
          </div>
        </div>`).join("");
      body = `<div class="small">${d.total} ${d.client ? `${esc(d.client)} ` : ""}projects${summary ? ` -- ${summary}` : " -- all consistent"}. Hover a row for its stored values.</div>
        <div style="height:6px;"></div>
        <div style="max-height:260px; overflow:auto;">${rows}</div>`;
    }
    return `
      <div class="card">
        <div style="font-weight:700;">Project CRS Audit</div>
        <div class="small" style="margin-top:4px;">Each project's CRS (set in project settings) is the source of truth for its data.</div>
        <div style="height:8px;"></div>
        ${body}
        <div class="row" style="margin-top:8px;">
          <button class="btn" data-act="run-audit">${d ? "Re-check" : "Check all projects"}</button>
        </div>
      </div>
      <div style="height:10px;"></div>`;
  }

  function render() {
    if (!container) return;
    const profile = svc.getProjectProfile();
    const customRows = svc.listCustomCRS()
      .map((c) => `
        <div class="proj-custom-row">
          <div class="small" title="${esc(c.proj4)}">${esc(c.code)} - ${esc(c.name)}</div>
          <button class="iconbtn danger" data-act="remove-crs" data-code="${esc(c.code)}">x</button>
        </div>
      `)
      .join("");

    container.innerHTML = `
      ${auditCardHtml()}
      <div class="card">
        <div style="font-weight:700;">Project Geodetic Profile</div>
        <div style="height:8px;"></div>
        <div class="kv"><span>Profile name</span><input id="pm-profile-name" type="text" value="${esc(profile.name)}" style="width:180px;" /></div>
        <div class="kv"><span>Horizontal CRS</span>${crsPickerField("pm-h-crs", profile.horizontalCRS, { style: "width:240px" })}</div>
        <div class="kv"><span>Vertical datum</span><input id="pm-v-datum" type="text" value="${esc(profile.verticalDatum)}" style="width:180px;" /></div>
        <div class="kv"><span>Geoid model</span><input id="pm-geoid" type="text" value="${esc(profile.geoidModel)}" style="width:180px;" /></div>
        <div class="kv">
          <span>Linear units</span>
          <select id="pm-linear-units" style="width:180px;">
            <option value="meters" ${profile.linearUnits === "meters" ? "selected" : ""}>meters</option>
            <option value="feet" ${profile.linearUnits === "feet" ? "selected" : ""}>feet</option>
            <option value="us-survey-feet" ${profile.linearUnits === "us-survey-feet" ? "selected" : ""}>us-survey-feet</option>
          </select>
        </div>
        <div class="kv">
          <span>Angular units</span>
          <select id="pm-angular-units" style="width:180px;">
            <option value="degrees" ${profile.angularUnits === "degrees" ? "selected" : ""}>degrees</option>
            <option value="radians" ${profile.angularUnits === "radians" ? "selected" : ""}>radians</option>
          </select>
        </div>
        <div class="row" style="margin-top:8px;">
          <button class="btn primary" data-act="save-profile">Save profile</button>
          <button class="btn" data-act="reset-profile">Reset</button>
        </div>
      </div>

      <div style="height:10px;"></div>

      <div class="card">
        <div style="font-weight:700;">Custom CRS (PROJ4)</div>
        <div style="height:8px;"></div>
        <div class="row">
          <input id="pm-custom-code" type="text" placeholder="Code (e.g. EPSG:6349)" style="width:140px;" />
          <input id="pm-custom-name" type="text" placeholder="Display name" style="width:180px;" />
        </div>
        <div style="height:8px;"></div>
        <textarea id="pm-custom-proj4" rows="3" placeholder="+proj=... +datum=... +units=m ..."></textarea>
        <div class="row" style="margin-top:8px;">
          <button class="btn" data-act="add-crs">Add custom CRS</button>
        </div>
        <div style="height:8px;"></div>
        ${customRows || `<div class="small">No custom CRS definitions.</div>`}
      </div>

      <div style="height:10px;"></div>

      <div class="card">
        <div style="font-weight:700;">Transform Preview</div>
        <div style="height:8px;"></div>
        <div class="row">
          ${crsPickerField("pm-from-crs", lastPreviewState.from || "EPSG:4326", { style: "width:240px" })}
          <span class="small">to</span>
          ${crsPickerField("pm-to-crs", lastPreviewState.to || "EPSG:3857", { style: "width:240px" })}
        </div>
        <div style="height:8px;"></div>
        <div class="row">
          <input id="pm-x" type="number" step="any" placeholder="X / Lon" style="width:180px;" />
          <input id="pm-y" type="number" step="any" placeholder="Y / Lat" style="width:180px;" />
          <button class="btn" data-act="preview-transform">Transform</button>
        </div>
        <div id="pm-preview-out" class="small" style="margin-top:8px;">(no result)</div>
      </div>
    `;
    enhanceCrsPickers(container, app);
  }

  function readProfileInput() {
    return {
      name: container.querySelector("#pm-profile-name")?.value?.trim() || "Default Hydro Profile",
      horizontalCRS: container.querySelector("#pm-h-crs")?.value || "EPSG:4326",
      verticalDatum: container.querySelector("#pm-v-datum")?.value?.trim() || "MSL",
      geoidModel: container.querySelector("#pm-geoid")?.value?.trim() || "None",
      linearUnits: container.querySelector("#pm-linear-units")?.value || "meters",
      angularUnits: container.querySelector("#pm-angular-units")?.value || "degrees",
    };
  }

  function onProfileFieldChange(e) {
    if (e.target?.id === "pm-h-crs") {
      svc.setProjectProfile(readProfileInput());
    }
  }

  function onClick(e) {
    const btn = e.target?.closest?.("[data-act]");
    if (!btn) return;
    const act = btn.dataset.act;

    if (act === "run-audit") {
      loadAudit();
      return;
    }

    if (act === "save-profile") {
      svc.setProjectProfile(readProfileInput());
      app.ui.toast("Projection profile saved");
      return;
    }

    if (act === "reset-profile") {
      svc.resetProjectProfile();
      app.ui.toast("Projection profile reset");
      render();
      return;
    }

    if (act === "add-crs") {
      const code = container.querySelector("#pm-custom-code")?.value || "";
      const name = container.querySelector("#pm-custom-name")?.value || "";
      const proj = container.querySelector("#pm-custom-proj4")?.value || "";
      try {
        svc.registerCRS({ code, name, proj4: proj });
        app.ui.toast(`Added CRS: ${String(code).toUpperCase()}`);
        render();
      } catch (err) {
        console.error("[ProjectionManager] add CRS failed", err);
        app.ui.toast("Failed to add CRS");
      }
      return;
    }

    if (act === "remove-crs") {
      const code = btn.dataset.code;
      if (!code) return;
      svc.removeCustomCRS(code);
      app.ui.toast(`Removed CRS: ${code}`);
      render();
      return;
    }

    if (act === "preview-transform") {
      const from = container.querySelector("#pm-from-crs")?.value || "EPSG:4326";
      const to = container.querySelector("#pm-to-crs")?.value || "EPSG:3857";
      const x = Number(container.querySelector("#pm-x")?.value);
      const y = Number(container.querySelector("#pm-y")?.value);
      const outEl = container.querySelector("#pm-preview-out");
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        if (outEl) outEl.textContent = "Enter numeric X/Y values.";
        return;
      }
      try {
        const out = svc.transformCoord([x, y], from, to);
        if (outEl) outEl.textContent = `${out[0].toFixed(3)}, ${out[1].toFixed(3)}`;
        lastPreviewState = {
          from,
          to,
          x: String(container.querySelector("#pm-x")?.value || ""),
          y: String(container.querySelector("#pm-y")?.value || ""),
        };
      } catch (err) {
        console.error("[ProjectionManager] preview transform failed", err);
        if (outEl) outEl.textContent = "Transform failed (check CRS defs).";
      }
    }
  }

  function mount(next) {
    container = next;
    render();
    container.onclick = onClick;
    container.onchange = onProfileFieldChange;
    if (!off.length) {
      off.push(app.events.on("projection:catalog:changed", render));
      off.push(app.events.on("projection:profile:changed", render));
    }
  }

  function snapshot() {
    if (container) {
      lastPreviewState = {
        from: container.querySelector("#pm-from-crs")?.value || "EPSG:4326",
        to: container.querySelector("#pm-to-crs")?.value || "EPSG:3857",
        x: String(container.querySelector("#pm-x")?.value || ""),
        y: String(container.querySelector("#pm-y")?.value || ""),
      };
    }
    const profile = svc.getProjectProfile();
    const customCRS = svc.listCustomCRS();
    return {
      profile,
      customCRS: customCRS.map((c) => ({ code: c.code, name: c.name, proj4: c.proj4 })),
      preview: { ...lastPreviewState },
    };
  }

  function restore(data) {
    if (!data || typeof data !== "object") return;

    if (Array.isArray(data.customCRS)) {
      const existingCustom = svc.listCustomCRS();
      existingCustom.forEach((c) => svc.removeCustomCRS(c.code));
      data.customCRS.forEach((def) => {
        try {
          svc.registerCRS(def);
        } catch (err) {
          console.error("[ProjectionManager] restore custom CRS failed", err);
        }
      });
    }

    if (data.profile && typeof data.profile === "object") {
      svc.setProjectProfile(data.profile);
    }

    if (data.preview && typeof data.preview === "object") {
      lastPreviewState = {
        from: String(data.preview.from || "EPSG:4326"),
        to: String(data.preview.to || "EPSG:3857"),
        x: String(data.preview.x || ""),
        y: String(data.preview.y || ""),
      };
    }

    if (!container) return;
    render();
    const fromSel = container.querySelector("#pm-from-crs");
    const toSel = container.querySelector("#pm-to-crs");
    const xEl = container.querySelector("#pm-x");
    const yEl = container.querySelector("#pm-y");
    // (the CRS pickers already render lastPreviewState.from/to)
    if (xEl) xEl.value = lastPreviewState.x;
    if (yEl) yEl.value = lastPreviewState.y;
  }

  function destroy() {
    off.splice(0).forEach((fn) => {
      try { fn?.(); } catch {}
    });
    if (container) container.onclick = null;
    container = null;
  }

  return { mount, destroy, snapshot, restore };
}
